// 国籍（Nationality）選択用の国・地域リスト。
// ISO 3166-1 alpha-2 コードを保持し、表示名は Intl.DisplayNames で
// 現在の言語（ja/en/zh）に応じて自動生成する。
// これにより巨大な多言語国名表を手で持たずに全世界の国・地域を網羅できる。
//
// 保存値は国コード（例: 'JP'）。表記ゆれが無く、管理画面での集計・表示も安定する。
// 旧データ（自由入力の国名文字列）が入っている場合に備え、呼び出し側で
// 「コードに一致しない既存値はそのまま表示」できるようにしている（PassportPage 側で対応）。

// ISO 3166-1 alpha-2（主要な国・地域を網羅）。必要に応じて追加可能。
export const COUNTRY_CODES: string[] = [
  'AD','AE','AF','AG','AI','AL','AM','AO','AR','AT','AU','AW','AZ',
  'BA','BB','BD','BE','BF','BG','BH','BI','BJ','BM','BN','BO','BR','BS','BT','BW','BY','BZ',
  'CA','CD','CF','CG','CH','CI','CL','CM','CN','CO','CR','CU','CV','CY','CZ',
  'DE','DJ','DK','DM','DO','DZ',
  'EC','EE','EG','ER','ES','ET',
  'FI','FJ','FM','FR',
  'GA','GB','GD','GE','GH','GM','GN','GQ','GR','GT','GW','GY',
  'HK','HN','HR','HT','HU',
  'ID','IE','IL','IN','IQ','IR','IS','IT',
  'JM','JO','JP',
  'KE','KG','KH','KI','KM','KN','KP','KR','KW','KZ',
  'LA','LB','LC','LI','LK','LR','LS','LT','LU','LV','LY',
  'MA','MC','MD','ME','MG','MH','MK','ML','MM','MN','MO','MR','MT','MU','MV','MW','MX','MY','MZ',
  'NA','NE','NG','NI','NL','NO','NP','NR','NZ',
  'OM',
  'PA','PE','PG','PH','PK','PL','PT','PW','PY',
  'QA',
  'RO','RS','RU','RW',
  'SA','SB','SC','SD','SE','SG','SI','SK','SL','SM','SN','SO','SR','SS','ST','SV','SY','SZ',
  'TD','TG','TH','TJ','TL','TM','TN','TO','TR','TT','TV','TW','TZ',
  'UA','UG','US','UY','UZ',
  'VA','VC','VE','VN','VU',
  'WS',
  'YE',
  'ZA','ZM','ZW',
];

// i18n の言語コードを Intl 用のロケールに対応づける。
const INTL_LOCALE: Record<string, string> = { ja: 'ja', en: 'en', zh: 'zh-Hans' };

export interface CountryOption { code: string; label: string; }

/**
 * 指定言語での「国コード→国名」オプション一覧を、国名の50音/アルファベット順で返す。
 * Intl.DisplayNames 非対応環境では国コードをそのまま label に用いる（フォールバック）。
 */
export function getCountryOptions(lang: string): CountryOption[] {
  const locale = INTL_LOCALE[lang] || 'en';
  let dn: Intl.DisplayNames | null = null;
  try {
    dn = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    dn = null;
  }
  const opts = COUNTRY_CODES.map((code) => ({
    code,
    label: (() => {
      try { return dn?.of(code) || code; } catch { return code; }
    })(),
  }));
  // 現在言語のロケールで国名ソート（日本語なら読み順に近い並び）。
  const collator = new Intl.Collator(locale);
  opts.sort((a, b) => collator.compare(a.label, b.label));
  return opts;
}

// 主要市場（よく使う居住国）。この順でリスト上部に固定し、すぐ選べるようにする。
// ホテル所在国の日本を先頭に、以降は東アジア→東南アジア→南アジア→北米→
// 中南米→欧州（＋北欧）→オセアニア→中東の順（ユーザー指定の23市場を国に展開）。
export const PRIORITY_COUNTRY_CODES: string[] = [
  'JP',                                 // 所在国
  'KR', 'CN', 'TW', 'HK',               // 東アジア
  'TH', 'SG', 'MY', 'ID', 'PH', 'VN',   // 東南アジア
  'IN',                                 // 南アジア
  'US', 'CA',                           // 北米
  'MX',                                 // 中南米
  'GB', 'FR', 'DE', 'IT', 'ES', 'RU',   // 欧州（主要）
  'SE', 'DK', 'NO', 'FI',               // 欧州（北欧）
  'AU',                                 // オセアニア
  'AE', 'SA', 'QA', 'KW', 'OM', 'BH',   // 中東（GCC6）
];

export interface CountryOptionGroup { label: string; options: CountryOption[]; }

/**
 * 居住国セレクト用に「主要市場（上部固定）」と「その他すべて（国名順）」の
 * 2グループを返す。主要国はすぐ選べるよう指定順を維持し、その他は国名順。
 * groupLabels は <optgroup> の見出し（呼び出し側で翻訳文言を渡す）。
 */
export function getGroupedCountryOptions(
  lang: string,
  groupLabels: { priority: string; others: string }
): CountryOptionGroup[] {
  const locale = INTL_LOCALE[lang] || 'en';
  let dn: Intl.DisplayNames | null = null;
  try { dn = new Intl.DisplayNames([locale], { type: 'region' }); } catch { dn = null; }
  const nameOf = (code: string) => {
    try { return dn?.of(code) || code; } catch { return code; }
  };

  // 主要国は指定順を維持（ソートしない）。
  const priority: CountryOption[] = PRIORITY_COUNTRY_CODES.map((code) => ({ code, label: nameOf(code) }));

  // その他は主要国を除いた全世界を国名順で。
  const prioritySet = new Set(PRIORITY_COUNTRY_CODES);
  const collator = new Intl.Collator(locale);
  const others: CountryOption[] = COUNTRY_CODES
    .filter((code) => !prioritySet.has(code))
    .map((code) => ({ code, label: nameOf(code) }))
    .sort((a, b) => collator.compare(a.label, b.label));

  return [
    { label: groupLabels.priority, options: priority },
    { label: groupLabels.others, options: others },
  ];
}
